import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests';
import { Button, Card, Table, Modal, Form, Badge, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Pagination } from '../../features/Pagination';

const ReferralMemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [referrals, setReferrals] = useState({ items: [], totalCount: 0 });
  const [accounts, setAccounts] = useState({ items: [], totalCount: 0 });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState({ items: [], totalCount: 0 });
  const [loading, setLoading] = useState(true);

  // Модалки
  const [showBanModal, setShowBanModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banDate, setBanDate] = useState('');

  useEffect(() => {
    loadMemberData();
    loadReferrals();
    loadAccounts();
  }, [id]);

  const loadMemberData = async () => {
    try {
      setLoading(true);
      const response = await apiRequests.referralProgram.getMember(id);
      setMember(response);
    } catch (e) {
      console.error('Ошибка загрузки участника:', e);
      toast.error('Ошибка загрузки данных участника');
    } finally {
      setLoading(false);
    }
  };

  const loadReferrals = async (count = 20, offset = 0) => {
    try {
      const response = await apiRequests.referralProgram.getMemberReferrals(id, count, offset);
      setReferrals(response);
    } catch (e) {
      console.error('Ошибка загрузки рефералов:', e);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await apiRequests.referralProgram.getAccounts(id);
      console.log('Счета загружены:', response);
      setAccounts(response);
    } catch (e) {
      console.error('Ошибка загрузки счетов:', e);
      toast.error('Ошибка загрузки счетов');
    }
  };

  const loadTransactions = async (accountId, count = 20, offset = 0) => {
    try {
      const response = await apiRequests.referralProgram.getTransactions(accountId, count, offset);
      setTransactions(response);
    } catch (e) {
      console.error('Ошибка загрузки транзакций:', e);
    }
  };

  const handleBan = async () => {
    if (!banReason || !banDate) {
      toast.error('Заполните все поля');
      return;
    }

    try {
      await apiRequests.referralProgram.banUser(id, {
        reason: banReason,
        bannedBefore: banDate,
      });
      toast.success('Участник заблокирован');
      setShowBanModal(false);
      loadMemberData();
    } catch (e) {
      console.error('Ошибка блокировки:', e);
      toast.error('Ошибка при блокировке участника');
    }
  };

  const handleUnban = async () => {
    try {
      await apiRequests.referralProgram.unbanUser(id);
      toast.success('Участник разблокирован');
      loadMemberData();
    } catch (e) {
      console.error('Ошибка разблокировки:', e);
      toast.error('Ошибка при разблокировке участника');
    }
  };

  const handleShowTransactions = (account) => {
    setSelectedAccount(account);
    loadTransactions(account.id);
    setShowTransactionsModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value, currencySymbol = '$') => {
    return `${value.toFixed(2)} ${currencySymbol}`;
  };

  const getUserName = (user) => {
    if (!user) return 'Без имени';
    const parts = [user.surname, user.name, user.patronymic].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Без имени';
  };

  if (loading) {
    return <div className="text-center p-5">Загрузка...</div>;
  }

  if (!member) {
    return <div className="text-center p-5">Участник не найден</div>;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Участник реферальной программы</h2>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-2"></i>
          Назад
        </Button>
      </div>

      {/* Основная информация */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Основная информация</h5>
          <div>
            {member.referralUser.banInfo ? (
              <Button variant="success" size="sm" onClick={handleUnban}>
                <i className="bi bi-unlock me-2"></i>
                Разблокировать
              </Button>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setShowBanModal(true)}>
                <i className="bi bi-lock me-2"></i>
                Заблокировать
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>ФИО:</strong> {getUserName(member.referralUser.user)}
              </p>
              <p>
                <strong>Email:</strong> {member.referralUser.user?.email || 'Не указан'}
              </p>
              <p>
                <strong>Телефон:</strong> {member.referralUser.user?.phone || 'Не указан'}
              </p>
              <p>
                <strong>Договор подписан:</strong>{' '}
                {formatDate(member.referralUser.agreementSignedAt)}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Всего заработано:</strong>{' '}
                <Badge bg="success" className="fs-6">
                  {formatCurrency(member.totalEarntInUSD)}
                </Badge>
              </p>
              <p>
                <strong>Всего клиентов:</strong> {member.totalCustomers}
              </p>
              <p>
                <strong>Всего заказов:</strong> {member.totalOrders}
              </p>
              {member.referralUser.banInfo && (
                <div className="alert alert-danger">
                  <strong>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Заблокирован
                  </strong>
                  <p className="mb-0">
                    До: {formatDate(member.referralUser.banInfo.bannedBefore)}
                  </p>
                  <p className="mb-0">Причина: {member.referralUser.banInfo.reason}</p>
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Табы */}
      <Tabs defaultActiveKey="referrals" className="mb-3">
        {/* Рефералы */}
        <Tab eventKey="referrals" title={`Рефералы (${referrals.totalCount})`}>
          <Card>
            <Card.Body>
              {referrals.items?.length === 0 ? (
                <p className="text-center">Нет рефералов</p>
              ) : (
                <>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>ФИО</th>
                        <th>Email</th>
                        <th>Договор подписан</th>
                        <th>Заработано</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.items?.map((ref) => (
                        <tr key={ref.referralUser.id}>
                          <td>{getUserName(ref.referralUser.user)}</td>
                          <td>{ref.referralUser.user?.email || 'Не указан'}</td>
                          <td>{formatDate(ref.referralUser.agreementSignedAt)}</td>
                          <td>{formatCurrency(ref.totalEarntInUSD)}</td>
                          <td>
                            <Link to={`/referral-program/members/${ref.referralUser.id}`}>
                              <Button variant="outline-primary" size="sm">
                                <i className="bi bi-eye"></i>
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {referrals.totalCount > 20 && (
                    <Pagination
                      count={referrals.totalCount}
                      itemsPerPage={20}
                      onChange={(page) => loadReferrals(20, 20 * page)}
                    />
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Счета */}
        <Tab eventKey="accounts" title={`Счета (${accounts.items?.length || 0})`}>
          <Card>
            <Card.Body>
              {accounts.items?.length === 0 ? (
                <p className="text-center">Нет счетов</p>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Валюта</th>
                      <th>Баланс</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.items?.map((account) => (
                      <tr key={account.id}>
                        <td>
                          {account.currency?.title} ({account.currency?.symbol})
                        </td>
                        <td>
                          <Badge bg={account.balance > 0 ? 'success' : 'secondary'}>
                            {formatCurrency(account.balance, account.currency?.symbol)}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowTransactions(account)}
                          >
                            <i className="bi bi-list-ul me-2"></i>
                            Транзакции
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Модалка блокировки */}
      <Modal show={showBanModal} onHide={() => setShowBanModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Заблокировать участника</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Причина блокировки</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Укажите причину блокировки"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Заблокировать до</Form.Label>
              <Form.Control
                type="datetime-local"
                value={banDate}
                onChange={(e) => setBanDate(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBanModal(false)}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleBan}>
            Заблокировать
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модалка транзакций */}
      <Modal
        show={showTransactionsModal}
        onHide={() => setShowTransactionsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Транзакции счета {selectedAccount?.currency?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {transactions.items?.length === 0 ? (
            <p className="text-center">Нет транзакций</p>
          ) : (
            <>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Тип</th>
                    <th>Сумма</th>
                    <th>Описание</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.items?.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{formatDate(transaction.createdAt)}</td>
                      <td>{transaction.discriminator}</td>
                      <td
                        className={
                          transaction.sum > 0 ? 'text-success' : 'text-danger'
                        }
                      >
                        {transaction.sum > 0 ? '+' : ''}
                        {formatCurrency(
                          transaction.sum,
                          selectedAccount?.currency?.symbol,
                        )}
                      </td>
                      <td>{transaction.description || '-'}</td>
                      <td>
                        {transaction.isCanceled ? (
                          <Badge bg="danger">Отменена</Badge>
                        ) : (
                          <Badge bg="success">Активна</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {transactions.totalCount > 20 && (
                <Pagination
                  count={transactions.totalCount}
                  itemsPerPage={20}
                  onChange={(page) =>
                    loadTransactions(selectedAccount.id, 20, 20 * page)
                  }
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransactionsModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReferralMemberDetail;
